import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import toast from "../components/DismissibleToast.js";
import { formatReturnStatus } from "../pages/GroupPOA/GroupPoAUtils.js";
import {
  setActiveTab,
  setCachedLocation,
  setIsClientFacing,
  setIsSystemAdmin,
  setIsTenantAdmin,
  setIsViewModeOn,
  setLastPostRequest,
  setLastRequest,
  setPeriodIndex,
  setTenantID,
  setTimerID,
  setTooltips,
  setUserData,
  setUserGroupRole,
  setUserReadonly,
} from "../store/slice/usageInfoSlice.js";
import { store } from "../store/store.js";
import { processDateJS } from "../utils/dateProcessor";
import {
  getGroupDataSync,
  resetGroupDataSync,
  setGroupDataSync,
  waitForGroupDataHydration,
} from "./storage/groupDataStorage";
dayjs.extend(utc);

export const LOCAL_KEYS = {
  groupJson: "json",
  selectedPeriodIndex: "poaIndex",
  cachedLocation: "cachedLocation",
  userReadonly: "userReadonly",
  user: "user",
  lastRequest: "lastRequest",
  timerID: "timerID",
  lastPostRequest: "lastPostRequest",
  isViewModeOn: "isViewModeOn",
  tooltips: "tooltips",
  tenantId: "tenantId",
  selectedTenantId: "selectedTenantId",
  isClientFacing: "isClientFacing",
};

export const getGroupData = () => {
  return getGroupDataSync();
};

export const setGroupData = (json) => {
  setGroupDataSync(json).catch((error) => {
    console.error("Failed to persist groupData to IndexedDB", error);
    toast.error(
      "Failed to save data. Your changes may not persist after a page refresh.",
    );
  });
};

export const setSelectedPeriodData = (properties, merge = false) => {
  const periodIndex = getPoaIndex();

  if (periodIndex === -1) {
    console.error("Error setting selected period data, no selected period");
    return;
  }

  const tmpData = { ...getGroupData() };
  const tmpPeriod = {
    ...(merge ? tmpData.periods_of_account[periodIndex] : {}),
    ...properties,
  };
  tmpData.periods_of_account[periodIndex] = tmpPeriod;
  setGroupData(tmpData);
};

export const getPeriodByIndex = (index) => {
  const groupData = getGroupData();

  if (!groupData) {
    console.error("Error getting period: no group data");
    return null;
  }

  if (
    (index ?? null) === null ||
    index < 0 ||
    index >= (groupData?.periods_of_account?.length ?? -1)
  ) {
    return null;
  }

  return groupData.periods_of_account[index];
};

export const setPoaIndex = (poaIndexValue) => {
  store.dispatch(setPeriodIndex(poaIndexValue));
};

export const getPoaIndex = () => {
  return store.getState().usageInfo.poaIndex ?? -1;
};

export const setViewMode = (viewModeStatus) => {
  store.dispatch(setIsViewModeOn(viewModeStatus));
};

export const getViewMode = () => {
  const currentPath = window.location.pathname?.split("/").at(-1).toLowerCase();
  const nonViewModePages = ["useraccess", "groupconfig"];

  if (nonViewModePages.includes(currentPath)) {
    store.dispatch(setIsViewModeOn(false));
  }

  return store.getState().usageInfo.isViewModeOn;
};

export const getSelectedPeriod = () => {
  return getPeriodByIndex(getPoaIndex()) ?? {};
};

export const getFormattedSelectedPeriod = () => {
  const selectedPeriod = getSelectedPeriod();
  if (!selectedPeriod) {
    return "No period selected";
  }

  if (!selectedPeriod?.period_start || !selectedPeriod?.period_end) {
    return "Selected Period Start or End dates are missing";
  }

  const start = selectedPeriod?.period_start;
  const end = selectedPeriod?.period_end;
  return `${processDateJS(start)?.format("DD/MM/YYYY")} - ${processDateJS(end)?.format("DD/MM/YYYY")}`;
};

export const getReturnStatus = () => {
  const selectedPeriod = getSelectedPeriod();
  if (!selectedPeriod) {
    return "No period selected";
  }
  if (!selectedPeriod?.return_status) {
    return "Selected Period Return Status is missing";
  }
  return formatReturnStatus(selectedPeriod);
};

export const draftReportingCompanyExists = () => {
  return getGroupData()?.reporting_company?.some((c) => c.status === "Draft");
};

export const getDraftReportingCompanies = () => {
  return getGroupData()?.reporting_company?.filter((c) => c.status === "Draft");
};

export const getRepCo = () => {
  const selectedPeriod = getSelectedPeriod();
  const draftReportingCompanies = getDraftReportingCompanies();

  if (!selectedPeriod) {
    return "No period selected";
  }
  if (!selectedPeriod?.reporting_company && !draftReportingCompanyExists()) {
    return "No Active Reporting Company";
  }

  const ReportingCo =
    selectedPeriod?.reporting_company ??
    draftReportingCompanies?.map((c) => c.company_name).join(" , ");
  return ReportingCo;
};
/**
 *
 * @returns the group name
 */
export const getGroupName = () => {
  const groupData = getGroupData();
  return groupData?.group_display_name ?? groupData?.group_name ?? "";
};

/**
 *
 * @returns the group ID
 */
export const getGroupID = () => {
  const { groupID, group_name } = getGroupData() ?? {};
  return groupID ?? group_name ?? "";
};

/**
 * Returns all the information relating to the period of accounts
 * @returns
 */
export const getPeriodOfAccounts = () => {
  return getGroupData()?.periods_of_account ?? {};
};

/**
 * A helper function to get the years for which we have PoA data
 */
export const getPoAYears = () => {
  return Object.keys(getPeriodOfAccounts())?.map((poa) => poa?.year) ?? [];
};

export const getPOAEndDates = () => {
  const data = getGroupData();
  const POAEndDates = data?.periods_of_account?.map((poa) => {
    return processDateJS(poa?.period_end)?.format("DD/MM/YYYY");
  });
  return POAEndDates;
};

export const getFirstPOAStartDate = () => {
  return getGroupData()?.model_first_period_start || undefined;
};

export const getLastNavigatedLocation = () => {
  return store.getState().usageInfo.cachedLocation || "/";
};

export const setLastNavigatedLocation = (newPage) => {
  store.dispatch(setCachedLocation(newPage?.toLocaleLowerCase() ?? "/"));
};

export const getPeriodIsReadonly = (period, enabledWhenFinalised = false) => {
  if (!period) return null;

  const isViewModeOn = getViewMode();
  const { isFinalised, isSubmitted } = period?.return_status ?? {};

  return (isFinalised && !enabledWhenFinalised) || isSubmitted || isViewModeOn;
};

export const getCurrentPeriodIsReadonly = (enabledWhenFinalised = false) => {
  return getPeriodIsReadonly(getSelectedPeriod(), enabledWhenFinalised);
};

export const getUserIsReadonly = () => {
  return store.getState().usageInfo.userReadonly ?? false;
};

export const isSubmissionOverdue = (selectedPoA) => {
  return processDateJS(new Date()).isAfter(
    processDateJS(selectedPoA?.period_end)?.add(12, "month"),
  );
};

export const canProgressCalculation = () => {
  const currentPath = window.location.pathname?.split("/").at(-1).toLowerCase();

  return { lastCompletedPoACalcs: currentPath };
};

export const disableProgressInViewMode = () => {
  const isViewModeOn = getViewMode();
  if (!isViewModeOn) return false;

  const currentPoA = getSelectedPeriod();
  const lastCompletedPoACalcs = currentPoA?.lastCompletedPoACalcs;
  const currentPath = window.location.pathname?.split("/").at(-1).toLowerCase();

  return (
    !lastCompletedPoACalcs ||
    (currentPath === lastCompletedPoACalcs && currentPath !== "editcfd")
  );
};

export const initializeGroupContextState = () => {
  const localJson = getGroupData();

  if (localJson) {
    if (!localJson?.group_name) {
      toast("No cached group", { icon: "⚠️" });
    }

    const selected_period_index = JSON.parse(
      sessionStorage.getItem(LOCAL_KEYS.selectedPeriodIndex),
    );
    if (selected_period_index !== null && selected_period_index !== undefined) {
      setPoaIndex(selected_period_index);
    }
    return;
  }

  setLastNavigatedLocation("/");
  setPoaIndex(-1);
};

export const hydrateGroupDataState = async () => {
  await waitForGroupDataHydration();
  initializeGroupContextState();
};

export const getPageTooltips = (page) => {
  const allTooltips = store.getState().usageInfo.tooltips ?? {};

  return allTooltips[page];
};

export const resetGroupData = () => {
  store.dispatch(setTenantID(null));
  store.dispatch(setIsClientFacing(false));
  store.dispatch(setActiveTab(null));
  store.dispatch(setIsViewModeOn(false));
  store.dispatch(setLastRequest(null));
  store.dispatch(setLastPostRequest(null));
  store.dispatch(setPeriodIndex(-1));
  store.dispatch(setTimerID(null));
  store.dispatch(setUserReadonly(false));
  store.dispatch(setCachedLocation("/"));
  store.dispatch(setTooltips(null));
  store.dispatch(setIsTenantAdmin(false));
  store.dispatch(setUserGroupRole(null));
  resetGroupDataSync();
};

export const resetDataStore = () => {
  resetGroupData();
  store.dispatch(setUserData(null));
  store.dispatch(setIsSystemAdmin(false));
};
